/**
 * Protocol Card Component
 */

import { FolderOpen, ListChecks, FileText, ChevronRight } from 'lucide-react';
import { ProtocolStatusBadge } from './ProtocolStatusBadge';
import type { Protocol } from '../../types';

interface ProtocolCardProps {
  protocol: Protocol;
  onClick?: () => void;
}

export function ProtocolCard({ protocol, onClick }: ProtocolCardProps) {
  const formattedDate = new Date(protocol.updated_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg group-hover:bg-primary-100 transition-colors">
            <FolderOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
              {protocol.protocol_number}
            </h3>
            {protocol.phase && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{protocol.phase}</p>
            )}
          </div>
        </div>
        <ProtocolStatusBadge status={protocol.status} />
      </div>

      {protocol.protocol_title && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{protocol.protocol_title}</p>
      )}

      <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400 mb-3">
        {protocol.sponsor_name && (
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">{protocol.sponsor_name}</span>
        )}
        {protocol.therapeutic_area && (
          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">{protocol.therapeutic_area}</span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <ListChecks className="w-4 h-4" />
            <span>{protocol.criteria_count || 0} criteria</span>
          </div>
          {protocol.review_count !== undefined && protocol.review_count > 0 && (
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>{protocol.review_count} reviews</span>
            </div>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-primary-600 transition-colors" />
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Updated {formattedDate}</p>
    </div>
  );
}
